import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Scissors, X, Loader2, RotateCcw, Check, Trash2, Eye } from "lucide-react";
import { api, type ClusterView, type SplitApplyView, type SplitProposalView } from "../../lib/api.js";

/**
 * Split a cluster that is really a bucket into one-page clusters.
 *
 * "Cluster" means ONE page (docs/CLUSTERING.md §1). A cluster whose members need
 * several pages is split into children under it; the original survives as the
 * hub. The model proposes, deterministic code validates, and nothing moves until
 * the operator has seen a dry-run:
 *
 *   Propose  → background job (can outrun CloudFront's 60s), polled here
 *   Preview  → dry-run apply: what moves, drift since the proposal, name clashes
 *   Apply    → writes the children and moves the keywords
 *   Revert   → puts every keyword back and removes the children
 *   Discard  → drops a proposal that is not applied
 */
export function SplitClusterDialog({ cluster, onClose }: { cluster: ClusterView; onClose: () => void }) {
  const qc = useQueryClient();
  const [banner, setBanner] = useState<{ kind: "ok" | "warn" | "error"; text: string } | null>(null);
  const [proposing, setProposing] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ id: string; result: SplitApplyView } | null>(null);

  const proposals = useQuery({
    queryKey: ["cluster-splits", cluster.id],
    queryFn: () => api.listSplitProposals(cluster.id),
  });

  // Pick up a proposal job that is already running (dialog reopened mid-run).
  useEffect(() => {
    api.splitProposeStatus(cluster.id).then((s) => { if (s.job?.status === "running") setProposing(true); }).catch(() => {});
  }, [cluster.id]);

  useEffect(() => {
    if (!proposing) return;
    const t = setInterval(async () => {
      try {
        const s = await api.splitProposeStatus(cluster.id);
        if (!s.job || s.job.status === "running") return;
        setProposing(false);
        if (s.job.status === "failed") setBanner({ kind: "error", text: s.job.error ?? "Proposal failed" });
        else setBanner({ kind: "ok", text: "Proposal ready. Review it, then Preview before applying." });
        qc.invalidateQueries({ queryKey: ["cluster-splits", cluster.id] });
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(t);
  }, [proposing, cluster.id, qc]);

  const propose = async () => {
    setBanner(null);
    try {
      const r = await api.proposeSplit(cluster.id);
      if (!r.ok) throw new Error(r.error ?? "Could not start");
      setProposing(true);
    } catch (e) {
      setBanner({ kind: "error", text: (e as Error).message });
    }
  };

  const act = async (id: string, what: "preview" | "apply" | "revert" | "discard") => {
    setBanner(null);
    setBusy(`${what}:${id}`);
    try {
      if (what === "preview") {
        const r = await api.applySplit(id, true);
        if (!r.ok || !r.result) throw new Error(r.error ?? "Preview failed");
        setPreview({ id, result: r.result });
      } else if (what === "apply") {
        if (!window.confirm("Apply this split? Keywords move into the new clusters now. It can be reverted.")) return;
        const r = await api.applySplit(id, false);
        if (!r.ok || !r.result) throw new Error(r.error ?? "Apply failed");
        setPreview(null);
        setBanner({ kind: "ok", text: `Applied: ${r.result.moved} keyword(s) moved into ${r.result.created.length} cluster(s).` });
        qc.invalidateQueries({ queryKey: ["pipeline"] });
      } else if (what === "revert") {
        if (!window.confirm("Revert this split? Every keyword goes back to this cluster and the sub-clusters are removed.")) return;
        const r = await api.revertSplit(id);
        if (!r.ok) throw new Error(r.error ?? "Revert failed");
        setBanner({ kind: "ok", text: `Reverted: ${r.result?.movedBack ?? 0} keyword(s) back, ${r.result?.removed ?? 0} sub-cluster(s) removed.` });
        qc.invalidateQueries({ queryKey: ["pipeline"] });
      } else {
        const r = await api.discardSplit(id);
        if (!r.ok) throw new Error(r.error ?? "Discard failed");
      }
      qc.invalidateQueries({ queryKey: ["cluster-splits", cluster.id] });
    } catch (e) {
      setBanner({ kind: "error", text: (e as Error).message });
    } finally {
      setBusy(null);
    }
  };

  const list = (proposals.data?.proposals ?? []).filter((p) => p.status !== "discarded");

  return (
    <>
      <div className="fixed inset-0 z-40 bg-ink/20 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto px-4 py-10">
        <div className="w-full max-w-3xl rounded-xl bg-white shadow-2xl">
          <header className="flex items-start justify-between gap-4 border-b border-line px-6 py-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-2xs font-semibold uppercase tracking-wider text-brand">
                <Scissors className="h-3.5 w-3.5" />
                Split cluster
              </div>
              <h2 className="mt-0.5 truncate text-lg font-semibold">
                {cluster.nameEn}
                <span className="ml-2 text-xs font-normal text-muted" dir="rtl">{cluster.nameHe}</span>
              </h2>
              <p className="mt-1 text-xs text-muted">
                A cluster is one page. If these keywords need several pages, split them into sub-clusters; this cluster stays as their hub.
              </p>
            </div>
            <button onClick={onClose} className="btn-ghost shrink-0" title="Close">
              <X className="h-4 w-4" />
            </button>
          </header>

          {banner && (
            <div className={`mx-6 mt-4 rounded-md px-3 py-2 text-xs ${
              banner.kind === "ok" ? "bg-accent/10 text-accent" : banner.kind === "warn" ? "bg-warn/10 text-warn" : "bg-danger/10 text-danger"
            }`} dir="auto">{banner.text}</div>
          )}

          <section className="space-y-4 px-6 py-4">
            <div className="flex items-center gap-3">
              <button onClick={propose} disabled={proposing} className="btn-primary text-sm disabled:opacity-50">
                {proposing ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Proposing…</> : <><Scissors className="h-3.5 w-3.5" /> Propose a split</>}
              </button>
              <span className="text-2xs text-muted">One model call, a few cents. Nothing moves until you apply.</span>
            </div>

            {proposals.isPending && <div className="text-xs text-muted">Loading proposals…</div>}
            {!proposals.isPending && list.length === 0 && !proposing && (
              <div className="text-xs text-muted">No proposals for this cluster yet.</div>
            )}

            {list.map((p) => (
              <ProposalCard
                key={p.id}
                p={p}
                busy={busy}
                preview={preview?.id === p.id ? preview.result : null}
                onAct={(what) => act(p.id, what)}
              />
            ))}
          </section>

          <footer className="flex justify-end gap-2 border-t border-line bg-canvas px-6 py-3">
            <button onClick={onClose} className="btn-ghost text-sm">Close</button>
          </footer>
        </div>
      </div>
    </>
  );
}

function ProposalCard({
  p, busy, preview, onAct,
}: {
  p: SplitProposalView;
  busy: string | null;
  preview: SplitApplyView | null;
  onAct: (what: "preview" | "apply" | "revert" | "discard") => void;
}) {
  const vol = new Map(p.inputKeywords.map((k) => [k.keyword, k.volume]));
  const is = (what: string) => busy === `${what}:${p.id}`;
  return (
    <div className="rounded-lg border border-line">
      <div className="flex items-center justify-between gap-2 border-b border-line bg-canvas px-3 py-2">
        <div className="text-xs">
          <span className="font-semibold">{p.inputCount} keywords → {p.subClusterCount} pages</span>
          <span className="ml-2 text-muted">{new Date(p.createdAt).toLocaleString()}</span>
          {p.estimatedCostUsd != null && <span className="ml-2 text-muted">${p.estimatedCostUsd.toFixed(3)}</span>}
          <span className={`ml-2 rounded px-1.5 py-0.5 text-2xs font-medium ${
            p.status === "applied" ? "bg-accent/10 text-accent" : p.validationOk ? "bg-line text-ink" : "bg-danger/10 text-danger"
          }`}>
            {p.status === "applied" ? "applied" : p.validationOk ? `validated${p.repair ? " (repaired)" : ""}` : "failed validation"}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {p.status === "proposed" && p.validationOk && (
            <>
              <button onClick={() => onAct("preview")} disabled={!!busy} className="btn-ghost text-2xs">
                {is("preview") ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />} Preview
              </button>
              <button onClick={() => onAct("apply")} disabled={!!busy || !preview} className="btn-primary text-2xs disabled:opacity-40" title={preview ? "" : "Preview first"}>
                {is("apply") ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Apply
              </button>
            </>
          )}
          {p.status === "proposed" && (
            <button onClick={() => onAct("discard")} disabled={!!busy} className="btn-ghost text-2xs" title="Discard this proposal">
              <Trash2 className="h-3 w-3" />
            </button>
          )}
          {p.status === "applied" && (
            <button onClick={() => onAct("revert")} disabled={!!busy} className="btn-ghost text-2xs">
              {is("revert") ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} Revert
            </button>
          )}
        </div>
      </div>

      {!p.validationOk && (
        <div className="px-3 py-2 text-2xs text-danger" dir="auto">{p.validation.problems.join(" · ")}</div>
      )}

      <ul className="divide-y divide-line">
        {p.output.sub_clusters.map((sc) => {
          const v = sc.keywords.reduce((a, k) => a + (vol.get(k.trim()) ?? 0), 0);
          return (
            <li key={sc.name_he} className="px-3 py-2">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium" dir="rtl">{sc.name_he}</span>
                <span className="shrink-0 text-2xs text-muted">{sc.keywords.length} kw · {v}/mo · {sc.intent}</span>
              </div>
              <div className="mt-1 flex flex-wrap gap-1" dir="rtl">
                {sc.keywords.map((k) => (
                  <span key={k} className={`rounded px-1.5 py-0.5 text-2xs ${k === sc.primary_keyword ? "bg-brand/10 font-medium text-brand" : "bg-canvas text-ink"}`}>
                    {k}
                  </span>
                ))}
              </div>
            </li>
          );
        })}
      </ul>

      {preview && (
        <div className="border-t border-line bg-accent/5 px-3 py-2 text-2xs" dir="auto">
          <div className="font-medium">Preview (nothing written): {preview.moved} keyword(s) would move into {preview.created.length} new cluster(s).</div>
          {preview.drift.goneFromCluster.length > 0 && (
            <div className="mt-1 text-warn">No longer in the cluster, skipped: {preview.drift.goneFromCluster.join(" · ")}</div>
          )}
          {preview.drift.newSinceProposal.length > 0 && (
            <div className="mt-1 text-warn">Joined after the proposal, stay on this cluster: {preview.drift.newSinceProposal.join(" · ")}</div>
          )}
          {preview.collisions.length > 0 && (
            <div className="mt-1 text-danger">
              Name already used (possible cannibalisation): {preview.collisions.map((c) => `"${c.proposed}" on ${c.usedBy}`).join(" · ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
